import { useState, useCallback, useRef, useEffect } from 'react';
import { isNative } from '../services/platformService';
import { hapticImpact } from '../services/hapticsService';

/**
 * Hook for implementing pull-to-refresh functionality
 * Works on both native (iOS/Android) and web platforms
 *
 * @param {Object} options
 * @param {Function} options.onRefresh - Async function to call when refresh is triggered
 * @param {number} options.threshold - Pull distance required to trigger refresh (default: 80)
 * @param {number} options.maxPull - Maximum pull distance (default: 120)
 * @param {boolean} options.disabled - Disable pull-to-refresh
 * @returns {Object} Pull-to-refresh state and handlers
 */
export function usePullToRefresh({
  onRefresh,
  threshold = 80,
  maxPull = 120,
  disabled = false,
} = {}) {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef(0);
  const currentY = useRef(0);
  const containerRef = useRef(null);

  // Check if element is scrolled to top
  const isAtTop = useCallback(() => {
    if (containerRef.current) {
      return containerRef.current.scrollTop <= 0;
    }
    return window.scrollY <= 0;
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (disabled || isRefreshing || !isAtTop()) return;

    startY.current = e.touches[0].clientY;
    currentY.current = startY.current;
  }, [disabled, isRefreshing, isAtTop]);

  const handleTouchMove = useCallback((e) => {
    if (disabled || isRefreshing || !startY.current) return;

    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - startY.current;

    // Only activate when pulling down from the top
    if (diff > 0 && isAtTop()) {
      // Apply resistance - pull distance increases slower as you pull further
      const resistance = 0.5;
      const actualPull = Math.min(diff * resistance, maxPull);

      setPullDistance(actualPull);
      setIsPulling(true);

      // Trigger haptic when crossing threshold
      if (actualPull >= threshold && pullDistance < threshold) {
        hapticImpact('light');
      }

      // Prevent default scroll when pulling
      if (actualPull > 10) {
        e.preventDefault();
      }
    }
  }, [disabled, isRefreshing, isAtTop, maxPull, threshold, pullDistance]);

  const handleTouchEnd = useCallback(async () => {
    if (disabled || isRefreshing || !isPulling) {
      startY.current = 0;
      return;
    }

    startY.current = 0;

    if (pullDistance >= threshold && onRefresh) {
      setIsRefreshing(true);
      setPullDistance(threshold); // Keep at threshold during refresh

      // Trigger haptic feedback
      hapticImpact('medium');

      try {
        await onRefresh();
      } catch (error) {
        console.error('[PullToRefresh] Refresh error:', error);
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
        setIsPulling(false);
      }
    } else {
      // Snap back
      setPullDistance(0);
      setIsPulling(false);
    }
  }, [disabled, isRefreshing, isPulling, pullDistance, threshold, onRefresh]);

  // Calculate progress (0-1)
  const progress = Math.min(pullDistance / threshold, 1);

  // Whether the threshold has been reached
  const canRefresh = pullDistance >= threshold;

  return {
    containerRef,
    isPulling,
    isRefreshing,
    pullDistance,
    progress,
    canRefresh,
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
  };
}

export default usePullToRefresh;
